import { createContext, useContext, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@/shared/ui/Button';
import { ErrorState } from '@/shared/ui/ErrorState';
import { type IUserProfile } from '../models/userProfile';
import { useInternalUser } from '../services/queries/user';
import { devBypassAuthUser, isDevAuthBypassEnabled } from './devMode';
import { RouteLoadingFallback } from './route-loading-fallback';

interface AuthContextValue {
  userId: string;
  userType: string;
  authId: string;
  email: string;
  internalUser: IUserProfile;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: auth0User, isAuthenticated, isLoading: isAuth0Loading, logout } = useAuth0();
  // WHY: effectiveAuth0User allows local development without Auth0 by using a mock user when bypass is enabled.
  const effectiveAuth0User = auth0User ?? (isDevAuthBypassEnabled ? devBypassAuthUser : undefined);
  const effectiveIsAuthenticated = isAuthenticated || isDevAuthBypassEnabled;

  // Fetch internal user from database using Auth0 sub and email
  const {
    data: internalUser,
    isLoading: isLoadingInternalUser,
    error
  } = useInternalUser(effectiveAuth0User?.sub ?? '', effectiveAuth0User?.email ?? '');

  // Show loading screen while Auth0 or internal user is loading
  if ((!isDevAuthBypassEnabled && isAuth0Loading) || (effectiveIsAuthenticated && isLoadingInternalUser)) {
    return <RouteLoadingFallback />;
  }

  // Show error screen if internal user fails to load
  if (effectiveIsAuthenticated && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[var(--surface-bg-primary)] gap-4 p-6">
        <ErrorState
          title="Unable to load user profile"
          description="We encountered an issue while retrieving your profile details. Please try refreshing the page or contact support."
        />
        <Button variant="secondary" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
          Sign out
        </Button>
      </div>
    );
  }

  // If not authenticated, provide null context (public routes handle this)
  if (!effectiveIsAuthenticated || !internalUser) {
    return <>{children}</>;
  }

  // Provide auth context to all children
  const value: AuthContextValue = {
    userId: internalUser.id,
    userType: internalUser.type || '',
    authId: effectiveAuth0User!.sub!,
    email: effectiveAuth0User!.email!,
    internalUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Returns null if user is not authenticated (for public routes)
 */
export function useAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

/**
 * Hook that requires authentication
 * Throws error if user is not authenticated or outside AuthProvider
 */
export function useRequireAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useRequireAuth must be used in authenticated routes');
  }
  return context;
}
